import { emptySplitApi } from "./emptyApi"

/**
 * Ad destinations — one row per ad with where its click lands.
 *
 * Backed by `GET /cli/ad-destinations`, the same endpoint `marathon
 * ad-destinations` and the `ad_destinations` MCP tool read, so the page, the
 * CLI and the agent see one list rather than three that drift.
 */

export interface TAdDestination {
  adId: string
  adName: string
  campaignName: string
  channel: string
  /** Platform delivery status, e.g. ACTIVE, PAUSED. */
  status: string
  /** Destination URL on the creative. Empty for catalog/dynamic ads and some placements. */
  url: string
  /** Raw url_tags as the platform reports them (JSON array or query string). */
  urlTags: string
  hasUrlTags: boolean
  spend: number
  impressions: number
  clicks: number
  /** Clicks / impressions as a percentage, null with no impressions. */
  ctr: number | null
}

export interface TAdDestinationsResponse {
  range: string
  ads: TAdDestination[]
}

export interface TAdDestinationsArgs {
  /** Company slug or id. */
  company: string
  range?: string
  /** active (server default), paused or all. */
  status?: string
  channel?: string
  /** Ad name contains. */
  search?: string
  limit?: number
}

/**
 * Query string for the endpoint. Optional params are omitted rather than sent
 * empty, because the API validates `status`, `channel` and `limit` when they
 * are present and a blank value is a 400 rather than a no-op.
 */
export function buildAdDestinationsParams({
  company,
  range,
  status,
  channel,
  search,
  limit,
}: TAdDestinationsArgs): Record<string, string> {
  return {
    company,
    ...(range ? { range } : {}),
    ...(status ? { status } : {}),
    ...(channel ? { channel } : {}),
    ...(search?.trim() ? { search: search.trim() } : {}),
    ...(limit ? { limit: String(limit) } : {}),
  }
}

export const adDestinationsApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getAdDestinations: builder.query<
      TAdDestinationsResponse,
      TAdDestinationsArgs
    >({
      query: args => ({
        url: "/cli/ad-destinations",
        params: buildAdDestinationsParams(args),
      }),
    }),
  }),
})

export const { useGetAdDestinationsQuery } = adDestinationsApi
